import React, { useState, useRef, useEffect } from 'react';
import { CheckCircle, X } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import logger from '../../utils/logger';
import ConsentTypeSelector from './consent/ConsentTypeSelector';
import ConsentDetailsForm from './consent/ConsentDetailsForm';
import ConsentVideoRecorder from './consent/ConsentVideoRecorder';
import ConsentReviewSubmit from './consent/ConsentReviewSubmit';
import ConsentSuccess from './consent/ConsentSuccess';

const VideoConsentRecorder = ({ patientId, patientName, onConsentRecorded, onClose }) => {
  const { t } = useLanguage();
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const [consentTypes, setConsentTypes] = useState({});
  const [selectedType, setSelectedType] = useState('');
  const [step, setStep] = useState(1);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [stream, setStream] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [consentId, setConsentId] = useState(null);

  const [formData, setFormData] = useState({
    procedure_name: '',
    procedure_date: '',
    attending_physician: '',
    witness_name: '',
    witness_relationship: '',
    interpreter_used: false,
    interpreter_language: '',
    patient_language: 'English',
    mental_capacity_confirmed: true,
    risks_explained: [],
    benefits_explained: [],
    alternatives_explained: [],
    questions_answered: true,
    patient_questions: '',
    additional_notes: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchConsentTypes();
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  useEffect(() => {
    let interval;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  const fetchConsentTypes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/healthtrack/video-consent/types`, {
        credentials: 'include'
      });
      const data = await response.json();
      setConsentTypes(data.categories || {});
    } catch (err) {
      logger.error('Error fetching consent types:', err);
    }
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { width: 1280, height: 720, facingMode: 'user' },
        audio: true
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      setError('Unable to access camera/microphone. Please grant permissions.');
    }
  };

  const startRecording = () => {
    if (!stream) return;
    chunksRef.current = [];
    const mediaRecorder = new MediaRecorder(stream, { mimeType: 'video/webm' });
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    mediaRecorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      setRecordedBlob(blob);
    };
    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start();
    setIsRecording(true);
    setRecordingTime(0);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  const createConsentRecord = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/healthtrack/video-consent/record`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient_id: patientId,
          consent_type: selectedType,
          ...formData
        })
      });
      const data = await response.json();
      if (data.consent_id) {
        setConsentId(data.consent_id);
        setStep(3);
        await startCamera();
      } else {
        setError(data.detail || 'Failed to create consent record');
      }
    } catch (err) {
      setError('Failed to create consent record');
    }
    setLoading(false);
  };

  const uploadVideo = async () => {
    if (!recordedBlob || !consentId) return;
    setLoading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('video', recordedBlob, 'consent_video.webm');
      formDataUpload.append('duration_seconds', recordingTime.toString());
      const response = await fetch(`${API_URL}/api/healthtrack/video-consent/${consentId}/upload-video`, {
        method: 'POST',
        credentials: 'include',
        body: formDataUpload
      });
      const data = await response.json();
      if (data.legally_valid) {
        setStep(5);
        if (onConsentRecorded) onConsentRecorded(data);
      } else {
        setError(data.detail || 'Failed to upload video');
      }
    } catch (err) {
      setError('Failed to upload video');
    }
    setLoading(false);
  };

  const getTypeInfo = () => {
    for (const [, types] of Object.entries(consentTypes)) {
      const found = types.find(t => t.type === selectedType);
      if (found) return found;
    }
    return null;
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-6">
      {[1, 2, 3, 4, 5].map((s) => (
        <React.Fragment key={s}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
            step === s ? 'bg-blue-600 text-white' :
            step > s ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'
          }`}>
            {step > s ? <CheckCircle className="h-5 w-5" /> : s}
          </div>
          {s < 5 && <div className={`w-12 h-1 ${step > s ? 'bg-green-500' : 'bg-gray-200'}`} />}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            {step < 5 ? 'Record Video Consent' : 'Consent Complete'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          {step < 5 && renderStepIndicator()}

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {step === 1 && (
            <ConsentTypeSelector
              consentTypes={consentTypes}
              selectedType={selectedType}
              onSelect={setSelectedType}
              onNext={() => setStep(2)}
              patientName={patientName}
            />
          )}
          {step === 2 && (
            <ConsentDetailsForm
              formData={formData}
              setFormData={setFormData}
              typeInfo={getTypeInfo()}
              loading={loading}
              onBack={() => setStep(1)}
              onSubmit={createConsentRecord}
            />
          )}
          {step === 3 && (
            <ConsentVideoRecorder
              videoRef={videoRef}
              stream={stream}
              isRecording={isRecording}
              recordedBlob={recordedBlob}
              recordingTime={recordingTime}
              procedureName={formData.procedure_name}
              onStartCamera={startCamera}
              onStartRecording={startRecording}
              onStopRecording={stopRecording}
              onNext={() => setStep(4)}
              onStreamReady={(s) => setStream(s)}
            />
          )}
          {step === 4 && (
            <ConsentReviewSubmit
              recordedBlob={recordedBlob}
              recordingTime={recordingTime}
              typeInfo={getTypeInfo()}
              formData={formData}
              loading={loading}
              onReRecord={() => { setRecordedBlob(null); setStep(3); startCamera(); }}
              onSubmit={uploadVideo}
            />
          )}
          {step === 5 && (
            <ConsentSuccess consentId={consentId} onClose={onClose} />
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoConsentRecorder;
